/*
 * @FilePath: /kitchen-offline-web/src/3DEngine/render/renderEngine/baseRenderEngine.js
 * @Author: Yongchao Wang
 * @Date: 2021-12-22 09:59:49
 * @LastEditors: Yongchao Wang
 * @LastEditTime: 2022-01-27 15:23:26
 * @Description:
 */
import { Scene } from '@babylonjs/core/scene';
import { Engine } from '@babylonjs/core/Engines/engine';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { AssetsManager } from '@babylonjs/core/Misc/assetsManager';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/index';
import { DefaultLoadingScreen } from '@babylonjs/core/Loading/loadingScreen';
import { Vector3 } from '@babylonjs/core/Maths/math';
import { addMeshTask, getReloadAssets } from '../utils/renderUtils';
// import '@babylonjs/core/Debug/debugLayer'; // Augments the scene with the debug methods
// import '@babylonjs/inspector';

export default class BaseRenderEngine {
  /**
   * @description: 初始化，将镜头灯光等参数初始化
   * @param {*} canvas
   * @return {*}
   * @author: Yongchao Wang
   */
  constructor(canvas) {
    this.canvas = canvas;
    DefaultLoadingScreen.prototype.displayLoadingUI = () => {
    };
    DefaultLoadingScreen.prototype.hideLoadingUI = () => {
    };
    const engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
    }, true);
    engine.displayLoadingUI();
    const scene = new Scene(engine);
    const camera = new ArcRotateCamera('camera', 1, 1, 1, new Vector3(0, 0, 0), scene);
    camera.minZ = 0.001;
    camera.maxZ = 100;
    camera.wheelDeltaPercentage = 0.03;
    camera.pinchDeltaPercentage = 0.03;

    camera.setPosition(new Vector3(2, 2, 2));
    camera.attachControl(canvas, false);
    scene.activeCamera.panningSensibility = 0;

    const { pointers } = camera.inputs.attached;
    if (pointers) {
      pointers.multiTouchPanning = false;
    }
    const light = new HemisphericLight('light', new Vector3(0, 1, 0), scene);
    this.windowVisiable = true;
    this.engine = engine;
    this.scene = scene;
    this.camera = camera;
    this.light = light;
    this.topPan = [];
    this.meshArray = [];
    this.templateJson = {};
    this.bottomRenderEngine = null;
    this.topRenderEngine = null;
    this.isRendering = false;
    // scene.debugLayer.show();
    this.calloutLineObj = {
      bottom: {},
      top: {},
      renderType: '',
    }; // 管理CalloutLine类，以便重绘等
    this.hideL1calloutLines = false;
    this.hideL3calloutLines = false;
    this.showCalloutLines = true;
    // this.showWorldAxis(1000);
    this.scene.clearColor = new Color4(0.0, 0.0, 0.0, 0);
    this.scene.imageProcessingConfiguration.contrast = 1.5;
    this.scene.imageProcessingConfiguration.exposure = 1;
    this.scene.imageProcessingConfiguration.toneMappingEnabled = true;

    const hdrTexture = new CubeTexture.CreateFromPrefilteredData(
      `${process.env.PUBLIC_URL}/textures/SpecularHDR.env`,
      this.scene,
    );
    this.scene.environmentTexture = hdrTexture;
  }

  /**
   * @description: 根据templateJson确定需要渲染的模型文件
   * @param {*} assets
   * @param {*} retryCount 重试次数，默认3次
   * @return {*}
   * @author: Yongchao Wang
   */
  loadAsset(assets, retryCount = 3) {
    return new Promise((resolve) => {
      if (retryCount === 0) {
        resolve();
      }
      const assetsManager = new AssetsManager(this.scene);
      // 相同的模型只加载一次其他的都用clone的方式加载
      const componentAssetsMap = {};
      const needCloneAssetsMap = {};
      assets.forEach((element) => {
        if (!element) {
          return;
        }
        addMeshTask(assetsManager, element, componentAssetsMap, needCloneAssetsMap);
      });
      const total = Object.keys(componentAssetsMap).length;
      let index = 0;
      const errorList = [];
      if (total === errorList.length) {
        resolve({
          error: errorList,
        });
      }
      assetsManager.onTaskSuccess = async (element) => {
        // element.name 是通过assetManage指定的fileName即业务中的componentBasic.id
        // eslint-disable-next-line no-param-reassign
        element.loadedMeshes[0].name = element.name;
        index += 1;
        if (total === index) {
          await this.finishAssetsTask(assets, needCloneAssetsMap, retryCount, errorList, resolve);
        }
      };
      assetsManager.onTaskError = async (element) => {
        index += 1;
        errorList.push(element.name);
        if (total === index) {
          await this.finishAssetsTask(assets, needCloneAssetsMap, retryCount, errorList, resolve);
        }
      };
      assetsManager.load();
      this.engine.stopRenderLoop();
    });
  }

  /**
     * @description: 错误的重试加载3次
     * @param {*} assets
     * @param {*} needCloneAssetsMap
     * @param {*} retryCount
     * @param {*} errorList
     * @param {*} resolve
     * @return {*}
     * @author: Yongchao Wang
     */
  async finishAssetsTask(assets, needCloneAssetsMap, retryCount, errorList, resolve) {
    let finalErrorList = errorList;
    if (errorList.length) {
      const reloadAssetIds = [];
      errorList.forEach((element) => {
        Object.keys(needCloneAssetsMap).forEach((el) => {
          if (needCloneAssetsMap[el] === element) {
            reloadAssetIds.push(el);
          }
        });
        reloadAssetIds.push(element);
      });
      const reloadAssets = getReloadAssets(assets.flat().filter((e) => e), reloadAssetIds);
      finalErrorList = (await this.loadAsset(reloadAssets, retryCount - 1)).error;
    }
    if (finalErrorList.length === 0) {
      Object.keys(needCloneAssetsMap).forEach((el) => {
        this.scene.getMeshByName(needCloneAssetsMap[el])?.clone(el);
      });
    }
    resolve({
      error: finalErrorList,
    });
  }
}

